import { onBeforeUnmount, onMounted, ref } from 'vue';

import { Logger } from '../services/logger';
import { addBroadcastMessageListener } from './broadcast.helpers';
import { IdleTimer, useIdleTimeout } from './idle.helpers';

const logger = Logger.createScoped('UpdateChecker');

export class UpdateChecker {
    private static localVersion = import.meta.env.VITE_APP_VERSION;
    static subscribers = new Set<() => void>();
    static isUpdateAvailable = false;

    static setup() {
        logger.info(`👋 from ZynoSuite ${this.localVersion}`);
        addBroadcastMessageListener<string>('fetchedLatestVersion', v => this.processLatestVersion(v));
    }

    private static processLatestVersion(latestVersion: string) {
        // if (!this.localVersion || this.localVersion === '0.0.0') return;
        if (isVersionGreater(latestVersion, this.localVersion)) {
            logger.info(`🎉 ZynoSuite ${latestVersion} update is available.`);
            this.isUpdateAvailable = true;
            for (const sub of this.subscribers) sub();
        }
    }
}

function isVersionGreater(maybeNewer: string, base: string) {
    const maybeNewerInts = maybeNewer.split('.').map(Number);
    const baseInts = base.split('.').map(Number);
    for (let i = 0; i < baseInts.length; i++) {
        if (maybeNewerInts[i]! > baseInts[i]!) {
            return true;
        }
    }
    return false;
}

export function useUpdateNotifier(onUpdateAvailable: () => void) {
    onMounted(() => {
        UpdateChecker.subscribers.add(onUpdateAvailable);
    });
    onBeforeUnmount(() => {
        UpdateChecker.subscribers.delete(onUpdateAvailable);
    });
}

export function useUpdateAvailableRef() {
    const retRef = ref(UpdateChecker.isUpdateAvailable);
    useUpdateNotifier(() => {
        retRef.value = true;
    });
    return retRef;
}

export function useIdleUpdateNotifier(timeoutMs: number, onIdleWithUpdates: () => void) {
    const checkConditions = () => {
        if (UpdateChecker.isUpdateAvailable && IdleTimer.getIdleMs() >= timeoutMs) {
            onIdleWithUpdates();
        }
    };
    useUpdateNotifier(checkConditions);
    useIdleTimeout({ timeoutMs, onIdle: checkConditions });
}

UpdateChecker.setup();
